import { z } from "zod";

export const ProductionMetricsSchema = z.object({
  increaseRate: z.number(),
  avgPrevious: z.number(),
  avgCurrent: z.number(),
  avgChange: z.number(),
  achievementRate: z.number(),
  unit: z.string(),
});

export type ProductionMetrics = z.infer<typeof ProductionMetricsSchema>;

export const LeadTimeMetricsSchema = z.object({
  reductionRate: z.number(),
  avgPrevious: z.number(),
  avgCurrent: z.number(),
  avgChange: z.number(),
  achievementRate: z.number(),
  unit: z.string(),
});

export type LeadTimeMetrics = z.infer<typeof LeadTimeMetricsSchema>;

export const KpiDailyStatisticsSchema = z.object({
  date: z.string(),
  dailyProduction: z.number().optional().nullable(),
  dailyLeadTime: z.number().optional().nullable(),
});

export const ProductionLogEntrySchema = z.object({
  time: z.string(),
  machineId: z.number(),
  machineName: z.string(),
  incrementalCount: z.number(),
  todayWorkloadCount: z.number(),
});

export type ProductionLogEntry = z.infer<typeof ProductionLogEntrySchema>;

export const ProductionLogResponseSchema = z.object({
  date: z.string(),
  totalSheets: z.number(),
  logs: z.array(ProductionLogEntrySchema),
});

export type ProductionLogResponse = z.infer<typeof ProductionLogResponseSchema>;

export const KpiStatisticsResponseSchema = z.object({
  companyId: z.number(),
  startDate: z.string(),
  endDate: z.string(),
  production: ProductionMetricsSchema,
  leadTime: LeadTimeMetricsSchema,
  dailyData: z.array(KpiDailyStatisticsSchema),
});

export type KpiStatisticsResponse = z.infer<typeof KpiStatisticsResponseSchema>;

// ─── 자사 KPI 슬롯(보고 항목) — GET /api/company/kpi/config ─────────────────
// 백엔드 CompanyKpi2SlotResponse 대응. 슬롯은 플러그인(SMART_WORKSHOP / SMART_FACTORY) 단위.
// sbizType/정부 코드는 SMART_WORKSHOP 만 값, kpiType 은 항상 값(SMART_FACTORY 는 네이티브).
export const CompanyKpiSlotSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  pluginId: z.number(),
  sbizType: z.string().nullable(),
  kpiType: z.string(),
  kpiFldCd: z.string().nullable(),
  kpiDtlCd: z.string().nullable(),
  kpiDtlNm: z.string().nullable(),
  unit: z.string(),
  baseValue: z.coerce.number(),
  goalValue: z.coerce.number(),
  aggregationInterval: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
});

export type CompanyKpiSlot = z.infer<typeof CompanyKpiSlotSchema>;

export const CompanyKpiSlotListSchema = z.array(CompanyKpiSlotSchema);

// PUT /api/company/kpi/slot/{id} 요청 — 미지정 필드는 미변경
export interface UpdateKpiSlotRequest {
  baseValue?: number;
  goalValue?: number;
  unit?: string;
  aggregationInterval?: string;
}

// POST /api/company/kpi/slot 요청 — 스마트 공장 슬롯 추가 (program 은 서버가 결정)
export interface CreateKpiSlotRequest {
  sbizType: string;
  baseValue: number;
  goalValue: number;
  unit: string;
  aggregationInterval: string;
}

// ─── 슬롯(선택 지표) 기반 대시보드 통계 — GET /api/company/kpi/statistics/slots ───
export const CompanyKpiSlotStatSchema = z.object({
  slotId: z.number(),
  group: z.string(), // "KPI1" | "KPI2"
  sbizType: z.string().nullable(),
  kpiType: z.string(),
  label: z.string(),
  unit: z.string(),
  baseValue: z.coerce.number(),
  goalValue: z.coerce.number(),
  avgCurrent: z.coerce.number(),
  avgChange: z.coerce.number(),
  changeRate: z.coerce.number(),
  achievementRate: z.coerce.number(),
  direction: z.string(), // "INCREASE" | "DECREASE"
  supported: z.boolean(),
  dailyData: z.array(z.object({ date: z.string(), value: z.coerce.number() })),
});

export type CompanyKpiSlotStat = z.infer<typeof CompanyKpiSlotStatSchema>;
export const CompanyKpiSlotStatListSchema = z.array(CompanyKpiSlotStatSchema);

// 회사 장비 — GET /api/company/kpi/machines (대시보드 장비 선택)
export const CompanyKpiMachineSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type CompanyKpiMachine = z.infer<typeof CompanyKpiMachineSchema>;
export const CompanyKpiMachineListSchema = z.array(CompanyKpiMachineSchema);

// ════════════════════════════════════════════════════════════════════════════
// KPI 2.6 카드 대시보드 (믹스패널 스타일) — /compute(동적계산) + /cards(저장)
// ════════════════════════════════════════════════════════════════════════════

/** 시간그룹 단위 (백엔드 MINUTE/HOUR/DAY/WEEK/MONTH). 분/시간은 시각 단위 — 백엔드 datetime 버킷 지원 후 데이터 연결. */
export type KpiInterval = "MINUTE" | "HOUR" | "DAY" | "WEEK" | "MONTH";
/** 기간 프리셋 (오늘 KST 기준 상대 + CUSTOM). 1D=오늘 하루(분/시간 단위 접근용). */
export type KpiPeriodPreset = "1D" | "7D" | "30D" | "3M" | "6M" | "12M" | "CUSTOM";

/** 조회 기간의 일수(span). CUSTOM 은 start~end 차이(+1, inclusive). 백엔드 spanDaysOf 와 동일. */
export const spanDays = (preset: KpiPeriodPreset, cs?: string, ce?: string): number => {
  switch (preset) {
    case "1D": return 1;
    case "7D": return 7;
    case "30D": return 30;
    case "3M": return 90;
    case "6M": return 183;
    case "12M": return 365;
    case "CUSTOM": {
      if (cs && ce) {
        const ms = new Date(ce).getTime() - new Date(cs).getTime();
        if (!Number.isNaN(ms)) return Math.max(1, Math.round(ms / 86_400_000) + 1);
      }
      return 30;
    }
    default:
      return 30;
  }
};

/**
 * 프리셋 → 날짜 범위(start~end, yyyy-MM-dd). 오늘 기준 상대. CUSTOM 은 null.
 * 백엔드 resolveRange 와 동일 일수(7D=오늘-6, 30D=오늘-29 …)로 채워 표시·편집용 날짜창에 사용.
 */
export const presetRange = (preset: KpiPeriodPreset): { start: string; end: string } | null => {
  if (preset === "CUSTOM") return null;
  const end = new Date();
  const start = new Date(end);
  switch (preset) {
    case "1D": break;
    case "7D": start.setDate(start.getDate() - 6); break;
    case "30D": start.setDate(start.getDate() - 29); break;
    case "3M": start.setMonth(start.getMonth() - 3); break;
    case "6M": start.setMonth(start.getMonth() - 6); break;
    case "12M": start.setMonth(start.getMonth() - 12); break;
  }
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { start: fmt(start), end: fmt(end) };
};

/**
 * 조회 기간(일수) → 허용 시간그룹 단위. 너무 촘촘한 단위는 막아 그래프 과밀 방지.
 * 백엔드 allowedIntervalsFor 와 동일 임계값(범위→단위 차단 규칙).
 *  ~1일=분·시간 / ~7일=시간·일 / ~31일=일·주 / ~6개월=주·월 / 그 이상=월
 */
export const allowedIntervals = (preset: KpiPeriodPreset, cs?: string, ce?: string): KpiInterval[] => {
  const d = spanDays(preset, cs, ce);
  if (d <= 1) return ["MINUTE", "HOUR"];
  if (d <= 7) return ["HOUR", "DAY"];
  if (d <= 31) return ["DAY", "WEEK"];
  if (d <= 183) return ["WEEK", "MONTH"];
  return ["MONTH"];
};
/** 차트 종류. */
export type KpiChartType = "BAR" | "LINE";
/** 시리즈 장비 범위. */
export type KpiMachineScope = "ALL" | "SPECIFIC";
/** 목표 방향 (달성률 부호). */
export type GoalDirection = "INCREASE" | "DECREASE";

/**
 * 회사 KPI 지표 = 정부 KPI 6종(SbizType). 백엔드 card_metric.metricType 과 1:1.
 * - computable: /compute 로 시계열·실측 자동 산출(그래프).
 * - !computable(수동형): 회사가 실측값 직접 입력(manualValue), 그래프 없이 달성률만.
 */
export const KPI_TYPE_OPTIONS = [
  // 라벨 = 그래프가 보여주는 실측값 이름(증가/단축/감소 같은 '효과' 표현 X — 정부보고 공식명 kpiDtlNm 은 백엔드 유지).
  // 생산량은 interval별 평균(시간당/분당/일당) — 백엔드 /compute 가 슬롯 interval 평균을 일별로 반환. 헤드라인은 그 평균들의 평균.
  { value: "SHEET_PRODUCTION", label: "생산량(매)", unit: "장", computable: true, direction: "INCREASE", aggregate: "AVG" },
  { value: "BOOK_PRODUCTION", label: "생산량(권)", unit: "권", computable: true, direction: "INCREASE", aggregate: "AVG" },
  // 가동률(%)은 base/goal 없으면 그래프가 안 나와, 대시보드는 가동시간(시간) 실측을 표시(백엔드 /compute 도 가동시간 반환).
  { value: "OPERATION_RATE", label: "가동시간", unit: "시간", computable: true, direction: "INCREASE", aggregate: "AVG" },
  { value: "ORDER_SHIPMENT_LEAD_TIME", label: "수주출하 리드타임", unit: "일", computable: true, direction: "DECREASE", aggregate: "AVG" },
  { value: "DELIVERY_COMPLIANCE_RATE", label: "납기 준수율", unit: "%", computable: true, direction: "INCREASE", aggregate: "AVG" },
  { value: "REVISION_REDUCTION", label: "설계변경 건수", unit: "건", computable: true, direction: "DECREASE", aggregate: "SUM" },
] as const;
export type KpiCardMetricType = (typeof KPI_TYPE_OPTIONS)[number]["value"];

/** 수동입력 지표(계산형 아님) 여부. 현재 6종 모두 계산형이라 사실상 false. */
export const isManualMetric = (metricType: string): boolean => {
  const opt = KPI_TYPE_OPTIONS.find((o) => o.value === metricType);
  return opt ? !opt.computable : false;
};

/** 지표 메타(라벨/단위/방향) 조회. */
export const metricOption = (metricType: string) =>
  KPI_TYPE_OPTIONS.find((o) => o.value === metricType);

/**
 * 그래프 시리즈(범례) 표기명 = 지표 라벨(실측값 이름).
 * 라벨이 곧 그래프 값(생산량/가동시간/리드타임 등)이라 범례도 동일.
 */
export const graphSeriesLabel = (metricType: string, fallback?: string): string => {
  return fallback ?? metricOption(metricType)?.label ?? metricType;
};

// ─── 동적 계산 — POST /api/company/kpi/compute ───────────────────────────────
// sbizType(SMART_WORKSHOP)/kpiType(SMART_FACTORY) 중 하나만 채운다 — 백엔드 ComputeMetricCommand 대응.
export interface ComputeMetricRequest {
  label: string;
  sbizType?: string;
  kpiType?: string;
  machineScope: KpiMachineScope;
  machineIds?: number[];
}
export interface ComputeRequest {
  interval: KpiInterval;
  periodPreset: KpiPeriodPreset;
  customStart?: string; // yyyy-MM-dd (CUSTOM 시)
  customEnd?: string;
  metrics: ComputeMetricRequest[];
}

export const ComputePointSchema = z.object({
  bucketStart: z.string(), // ISO datetime (일/주/월=자정, 시간=정시, 분=0초)
  value: z.coerce.number(),
});
export const ComputeSeriesSchema = z.object({
  label: z.string(),
  sbizType: z.string().nullable(),
  kpiType: z.string(),
  unit: z.string(),
  points: z.array(ComputePointSchema),
});
export const ComputeResponseSchema = z.object({
  interval: z.string(),
  start: z.string(),
  end: z.string(),
  buckets: z.array(z.string()),
  series: z.array(ComputeSeriesSchema),
});
export type ComputeResponse = z.infer<typeof ComputeResponseSchema>;
export type ComputeSeries = z.infer<typeof ComputeSeriesSchema>;

// ─── 카드 — GET/POST/PUT/DELETE /api/company/kpi/cards ───────────────────────
export const CardMetricSchema = z.object({
  id: z.number(),
  label: z.string().nullable(),
  metricType: z.string(),
  machineScope: z.string(),
  displayOrder: z.number(),
  machineIds: z.array(z.number()),
  baseValue: z.coerce.number().nullable(),
  goalValue: z.coerce.number().nullable(),
  goalDirection: z.string().nullable(),
  manualValue: z.coerce.number().nullable(),
});
export const MesDashboardCardSchema = z.object({
  id: z.number(),
  companyId: z.number(),
  // 카드 제목(옵션). null 이면 지표 이름으로 폴백 표시.
  title: z.string().nullable(),
  interval: z.string(),
  periodPreset: z.string(),
  customStart: z.string().nullable(),
  customEnd: z.string().nullable(),
  chartType: z.string(),
  displayOrder: z.number(),
  metrics: z.array(CardMetricSchema),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
});
export type MesDashboardCard = z.infer<typeof MesDashboardCardSchema>;
export type CardMetric = z.infer<typeof CardMetricSchema>;
export const MesDashboardCardListSchema = z.array(MesDashboardCardSchema);

/** 카드 생성/수정 요청 (SaveCardCommand). */
export interface SaveCardMetricRequest {
  label?: string;
  metricType: string;
  machineScope: KpiMachineScope;
  machineIds?: number[];
  baseValue?: number | null;
  goalValue?: number | null;
  goalDirection?: GoalDirection | null;
  manualValue?: number | null;
}
export interface SaveCardRequest {
  /** 카드 제목(옵션). null/미지정이면 지표 이름으로 폴백. */
  title?: string | null;
  interval: KpiInterval;
  periodPreset: KpiPeriodPreset;
  customStart?: string;
  customEnd?: string;
  chartType: KpiChartType;
  metrics: SaveCardMetricRequest[];
}
